import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiShoppingCart, FiCheck } = FiIcons;

function AvatarCustomization() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [activeTab, setActiveTab] = useState('avatars');

  // ... (rest of the avatar data remains the same)
  const avatarCharacters = {
    lion: { emoji: '🦁', name: 'Lion', cost: 0, category: 'Safari' },
    horse: { emoji: '🐴', name: 'Horse', cost: 20, category: 'Farm' },
    cow: { emoji: '🐄', name: 'Cow', cost: 20, category: 'Farm' },
    pig: { emoji: '🐷', name: 'Pig', cost: 20, category: 'Farm' },
    sheep: { emoji: '🐑', name: 'Sheep', cost: 20, category: 'Farm' },
    goat: { emoji: '🐐', name: 'Goat', cost: 20, category: 'Farm' },
    tiger: { emoji: '🐯', name: 'Tiger', cost: 25, category: 'Safari' },
    elephant: { emoji: '🐘', name: 'Elephant', cost: 25, category: 'Safari' },
    giraffe: { emoji: '🦒', name: 'Giraffe', cost: 25, category: 'Safari' },
    zebra: { emoji: '🦓', name: 'Zebra', cost: 25, category: 'Safari' },
    rhino: { emoji: '🦏', name: 'Rhino', cost: 25, category: 'Safari' },
    hippo: { emoji: '🦛', name: 'Hippo', cost: 25, category: 'Safari' },
    bear: { emoji: '🐻', name: 'Bear', cost: 30, category: 'Forest' },
    fox: { emoji: '🦊', name: 'Fox', cost: 30, category: 'Forest' },
    wolf: { emoji: '🐺', name: 'Wolf', cost: 30, category: 'Forest' },
    deer: { emoji: '🦌', name: 'Deer', cost: 30, category: 'Forest' },
    raccoon: { emoji: '🦝', name: 'Raccoon', cost: 30, category: 'Forest' },
    monkey: { emoji: '🐵', name: 'Monkey', cost: 35, category: 'Jungle' },
    gorilla: { emoji: '🦍', name: 'Gorilla', cost: 35, category: 'Jungle' },
    sloth: { emoji: '🦥', name: 'Sloth', cost: 35, category: 'Jungle' },
    penguin: { emoji: '🐧', name: 'Penguin', cost: 40, category: 'Arctic' },
    seal: { emoji: '🦭', name: 'Seal', cost: 40, category: 'Arctic' },
    panda: { emoji: '🐼', name: 'Panda', cost: 50, category: 'Rare' },
    koala: { emoji: '🐨', name: 'Koala', cost: 50, category: 'Rare' },
    unicorn: { emoji: '🦄', name: 'Unicorn', cost: 100, category: 'Legendary' },
    dragon: { emoji: '🐉', name: 'Dragon', cost: 150, category: 'Legendary' }
  };

  const hats = {
    'hat-cap': { emoji: '🧢', name: 'Baseball Cap', cost: 5 },
    'hat-crown': { emoji: '👑', name: 'Royal Crown', cost: 30 },
    'hat-wizard': { emoji: '🎩', name: 'Wizard Hat', cost: 25 },
    'hat-party': { emoji: '🎉', name: 'Party Hat', cost: 15 },
    'hat-cowboy': { emoji: '🤠', name: 'Cowboy Hat', cost: 20 },
    'hat-pirate': { emoji: '🏴‍☠️', name: 'Pirate Hat', cost: 35 },
    'hat-chef': { emoji: '👨‍🍳', name: 'Chef Hat', cost: 18 },
    'hat-santa': { emoji: '🎅', name: 'Santa Hat', cost: 25 },
    'hat-top': { emoji: '🎭', name: 'Top Hat', cost: 40 },
    'hat-beret': { emoji: '🇫🇷', name: 'French Beret', cost: 22 }
  };

  const accessories = {
    'acc-glasses': { emoji: '🤓', name: 'Smart Glasses', cost: 8 },
    'acc-sunglasses': { emoji: '😎', name: 'Cool Sunglasses', cost: 12 },
    'acc-bowtie': { emoji: '🎀', name: 'Bow Tie', cost: 10 },
    'acc-medal': { emoji: '🏅', name: 'Gold Medal', cost: 15 },
    'acc-necklace': { emoji: '📿', name: 'Pearl Necklace', cost: 20 },
    'acc-scarf': { emoji: '🧣', name: 'Winter Scarf', cost: 14 },
    'acc-watch': { emoji: '⌚', name: 'Smart Watch', cost: 25 },
    'acc-backpack': { emoji: '🎒', name: 'Adventure Backpack', cost: 18 },
    'acc-mask': { emoji: '🎭', name: 'Theater Mask', cost: 30 },
    'acc-flower': { emoji: '🌸', name: 'Flower Crown', cost: 16 },
    'acc-gem': { emoji: '💎', name: 'Magic Gem', cost: 45 },
    'acc-star': { emoji: '⭐', name: 'Star Badge', cost: 35 }
  };

  const backgrounds = {
    'bg-jungle': { emoji: '🌿', name: 'Jungle Paradise', cost: 20 },
    'bg-ocean': { emoji: '🌊', name: 'Ocean Depths', cost: 25 },
    'bg-space': { emoji: '🌌', name: 'Cosmic Space', cost: 40 },
    'bg-rainbow': { emoji: '🌈', name: 'Rainbow Land', cost: 35 },
    'bg-castle': { emoji: '🏰', name: 'Fairy Castle', cost: 50 },
    'bg-volcano': { emoji: '🌋', name: 'Volcano Island', cost: 45 }
  };

  const handleAvatarPurchase = (avatarId) => {
    const avatar = avatarCharacters[avatarId];
    if (state.ownedAvatars.includes(avatarId)) {
      dispatch({ type: 'UPDATE_AVATAR', payload: { character: avatarId } });
    } else if (state.tokens >= avatar.cost) {
      dispatch({ type: 'PURCHASE_AVATAR', payload: { id: avatarId, cost: avatar.cost } });
    }
  };

  const handleAccessoryPurchase = (accessoryId, accessoryType) => {
    const accessoryList = accessoryType === 'hat' ? hats : accessoryType === 'accessory' ? accessories : backgrounds;
    const accessory = accessoryList[accessoryId];

    if (state.unlockedAccessories.includes(accessoryId)) {
      const currentValue = state.avatar[accessoryType];
      dispatch({
        type: 'UPDATE_AVATAR',
        payload: { [accessoryType]: currentValue === accessoryId ? null : accessoryId }
      });
    } else if (state.tokens >= accessory.cost) {
      dispatch({ type: 'PURCHASE_ACCESSORY', payload: { id: accessoryId, cost: accessory.cost } });
      dispatch({ type: 'UPDATE_AVATAR', payload: { [accessoryType]: accessoryId } });
    }
  };

  const removeAccessory = (accessoryType) => {
    dispatch({ type: 'UPDATE_AVATAR', payload: { [accessoryType]: null } });
  };

  const getAvatarsByCategory = () => {
    const categories = {};
    Object.entries(avatarCharacters).forEach(([id, avatar]) => {
      if (!categories[avatar.category]) {
        categories[avatar.category] = [];
      }
      categories[avatar.category].push({ id, ...avatar });
    });
    return categories;
  };

  const avatarCategories = getAvatarsByCategory();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4 pt-24"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header - Removed back button and tokens display as they're in nav */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-kid text-jungle-700">Avatar Shop</h1>
        </motion.div>

        {/* Avatar Preview */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-6">Your Avatar</h2>
          <div className="flex justify-center">
            <div className="relative">
              <div
                className={`w-32 h-32 rounded-full flex items-center justify-center text-6xl border-4 border-safari-300 ${
                  state.avatar.background
                    ? 'bg-gradient-to-br from-blue-100 to-purple-100'
                    : 'bg-gradient-to-br from-jungle-100 to-safari-100'
                }`}
              >
                {avatarCharacters[state.avatar.character]?.emoji}
              </div>
              {state.avatar.hat && hats[state.avatar.hat] && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-3xl">
                  {hats[state.avatar.hat].emoji}
                </div>
              )}
              {state.avatar.accessory && accessories[state.avatar.accessory] && (
                <div className="absolute bottom-2 right-2 text-2xl">{accessories[state.avatar.accessory].emoji}</div>
              )}
              {state.avatar.background && backgrounds[state.avatar.background] && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-lg">
                  {backgrounds[state.avatar.background].emoji}
                </div>
              )}
            </div>
          </div>
          <div className="text-center mt-4">
            <h3 className="font-kid text-jungle-700 text-xl">{avatarCharacters[state.avatar.character]?.name}</h3>
            <div className="flex justify-center space-x-4 mt-2 text-sm font-comic text-jungle-600">
              <span>Owned Avatars: {state.ownedAvatars.length}</span>
              <span>Accessories: {state.unlockedAccessories.length}</span>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-2xl p-2 shadow-lg border-2 border-safari-200 flex space-x-2">
            {['avatars', 'hats', 'accessories', 'backgrounds'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-xl font-comic transition-all duration-200 ${
                  activeTab === tab ? 'bg-jungle-400 text-white shadow-lg' : 'text-jungle-700 hover:bg-jungle-100'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content based on active tab - keeping existing content */}
        <motion.div key={activeTab} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
          {activeTab === 'avatars' && (
            <div className="space-y-8">
              {Object.entries(avatarCategories).map(([category, avatars]) => (
                <div key={category}>
                  <h3 className="text-xl font-kid text-jungle-700 mb-4">{category} Animals</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {avatars.map((avatar) => {
                      const isOwned = state.ownedAvatars.includes(avatar.id);
                      const canAfford = state.tokens >= avatar.cost;
                      const isSelected = state.avatar.character === avatar.id;

                      return (
                        <motion.button
                          key={avatar.id}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleAvatarPurchase(avatar.id)}
                          className={`bg-white rounded-2xl p-4 shadow-lg border-2 transition-all duration-200 ${
                            isSelected
                              ? 'border-jungle-400 shadow-xl ring-2 ring-jungle-300'
                              : isOwned
                              ? 'border-green-400 hover:border-green-500'
                              : canAfford
                              ? 'border-safari-200 hover:border-safari-400'
                              : 'border-gray-200 opacity-50 cursor-not-allowed'
                          }`}
                          disabled={!isOwned && !canAfford}
                        >
                          <div className="text-4xl mb-2">{avatar.emoji}</div>
                          <div className="font-comic text-xs text-jungle-700 mb-1">{avatar.name}</div>
                          {isOwned ? (
                            <div className="flex items-center justify-center text-green-600">
                              <SafeIcon icon={FiCheck} className="text-sm mr-1" />
                              <span className="text-xs font-comic">Owned</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <span className="text-xs">🪙</span>
                              <span className="text-xs font-comic ml-1">{avatar.cost}</span>
                            </div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'hats' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-kid text-jungle-700">Hats Collection</h3>
                {state.avatar.hat && (
                  <button onClick={() => removeAccessory('hat')} className="text-sm font-comic text-red-500 hover:text-red-700">
                    Remove Hat
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {Object.entries(hats).map(([id, hat]) => {
                  const isUnlocked = state.unlockedAccessories.includes(id);
                  const canAfford = state.tokens >= hat.cost;
                  const isSelected = state.avatar.hat === id;

                  return (
                    <motion.button
                      key={id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAccessoryPurchase(id, 'hat')}
                      className={`bg-white rounded-2xl p-4 shadow-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-jungle-400 shadow-xl ring-2 ring-jungle-300'
                          : isUnlocked
                          ? 'border-green-400 hover:border-green-500'
                          : canAfford
                          ? 'border-safari-200 hover:border-safari-400'
                          : 'border-gray-200 opacity-50 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked && !canAfford}
                    >
                      <div className="text-2xl mb-2">{hat.emoji}</div>
                      <div className="font-comic text-xs text-jungle-700 mb-1">{hat.name}</div>
                      {isUnlocked ? (
                        <div className="flex items-center justify-center text-green-600">
                          <SafeIcon icon={FiCheck} className="text-sm mr-1" />
                          <span className="text-xs font-comic">Owned</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span className="text-xs">🪙</span>
                          <span className="text-xs font-comic ml-1">{hat.cost}</span>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'accessories' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-kid text-jungle-700">Accessories Collection</h3>
                {state.avatar.accessory && (
                  <button
                    onClick={() => removeAccessory('accessory')}
                    className="text-sm font-comic text-red-500 hover:text-red-700"
                  >
                    Remove Accessory
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {Object.entries(accessories).map(([id, accessory]) => {
                  const isUnlocked = state.unlockedAccessories.includes(id);
                  const canAfford = state.tokens >= accessory.cost;
                  const isSelected = state.avatar.accessory === id;

                  return (
                    <motion.button
                      key={id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAccessoryPurchase(id, 'accessory')}
                      className={`bg-white rounded-2xl p-4 shadow-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-jungle-400 shadow-xl ring-2 ring-jungle-300'
                          : isUnlocked
                          ? 'border-green-400 hover:border-green-500'
                          : canAfford
                          ? 'border-safari-200 hover:border-safari-400'
                          : 'border-gray-200 opacity-50 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked && !canAfford}
                    >
                      <div className="text-2xl mb-2">{accessory.emoji}</div>
                      <div className="font-comic text-xs text-jungle-700 mb-1">{accessory.name}</div>
                      {isUnlocked ? (
                        <div className="flex items-center justify-center text-green-600">
                          <SafeIcon icon={FiCheck} className="text-sm mr-1" />
                          <span className="text-xs font-comic">Owned</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span className="text-xs">🪙</span>
                          <span className="text-xs font-comic ml-1">{accessory.cost}</span>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'backgrounds' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-kid text-jungle-700">Background Themes</h3>
                {state.avatar.background && (
                  <button
                    onClick={() => removeAccessory('background')}
                    className="text-sm font-comic text-red-500 hover:text-red-700"
                  >
                    Remove Background
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(backgrounds).map(([id, background]) => {
                  const isUnlocked = state.unlockedAccessories.includes(id);
                  const canAfford = state.tokens >= background.cost;
                  const isSelected = state.avatar.background === id;

                  return (
                    <motion.button
                      key={id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAccessoryPurchase(id, 'background')}
                      className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-jungle-400 shadow-xl ring-2 ring-jungle-300'
                          : isUnlocked
                          ? 'border-green-400 hover:border-green-500'
                          : canAfford
                          ? 'border-safari-200 hover:border-safari-400'
                          : 'border-gray-200 opacity-50 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked && !canAfford}
                    >
                      <div className="text-4xl mb-3">{background.emoji}</div>
                      <div className="font-comic text-sm text-jungle-700 mb-2">{background.name}</div>
                      {isUnlocked ? (
                        <div className="flex items-center justify-center text-green-600">
                          <SafeIcon icon={FiCheck} className="text-sm mr-1" />
                          <span className="text-xs font-comic">Owned</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <span className="text-sm">🪙</span>
                          <span className="text-sm font-comic ml-1">{background.cost}</span>
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default AvatarCustomization;